import { Upload, message, Progress } from "antd";
import ImgCrop from "antd-img-crop";
import { useState } from "react";

import { serverUrl } from "../../nestserver";
import { uploadFile } from "../../services/upload.service";
import { resizeImageFile } from "../../utils/imageResize";

export default function ImageUploadButton({
  image,
  name,
  onUpload,
  ratio = 1,
  children,
  folder = "common",
  multiple = false,
  disabled = false,
  allowedTypes = ["image"],
  maxSize = 50,
  enableCrop = false,
  cropShape = "rect",
  maxDimension = null,
  compressQuality = 0.85,
}) {
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);

  async function beforeUpload(file) {
    if (disabled) {
      message.warning("Maximum limit reached");
      return false;
    }

    const typeConfig = {
      image: {
        mimeTypes: ["image/jpeg", "image/png"],
        label: "JPG/PNG images",
      },
      video: {
        mimeTypes: ["video/mp4", "video/quicktime"],
        label: "MP4 videos",
      },
      audio: {
        mimeTypes: ["audio/mpeg", "audio/mp3", "audio/wav"],
        label: "MP3/WAV audio files",
      },
    };

    let isValidType = false;
    let allowedLabels = [];

    allowedTypes.forEach((type) => {
      if (typeConfig[type]) {
        if (typeConfig[type].mimeTypes.includes(file.type)) {
          isValidType = true;
        }
        allowedLabels.push(typeConfig[type].label);
      }
    });

    if (!isValidType) {
      message.error(`You can only upload ${allowedLabels.join(", ")}!`);
      return false;
    }

    const isValidSize = file.size / 1024 / 1024 < maxSize;
    if (!isValidSize) {
      message.error(`File must be smaller than ${maxSize}MB!`);
      return false;
    }

    if (maxDimension && allowedTypes.includes("image")) {
      try {
        return await resizeImageFile(file, {
          maxDimension,
          quality: compressQuality,
        });
      } catch (e) {
        message.error("Failed to process image");
        return false;
      }
    }

    return true;
  }

  const uploadButton = (
    <div>
      {loading ? (
        <Progress type="circle" width={50} percent={progress} />
      ) : (
        children
      )}
    </div>
  );

  async function uploadImage(data) {
    try {
      setLoading(true);
      const res = await uploadFile(data, folder, setProgress);
      setLoading(false);
      if (res) {
        await onUpload(res.id, data.file);
        message.success("File uploaded successfully");
      }
    } catch (error) {
      setLoading(false);
      message.error(error.message);
    }
  }

  const uploadElement = (
    <Upload
      name={name}
      action={serverUrl + "/uploads"}
      showUploadList={false}
      customRequest={uploadImage}
      beforeUpload={beforeUpload}
      multiple={multiple}
      disabled={disabled}
    >
      {image ? (
        <img
          src={image}
          alt="thumbnail"
          style={{ width: "100%", aspectRatio: "1/1", objectFit: "cover" }}
        />
      ) : (
        uploadButton
      )}
    </Upload>
  );

  return (
    <div>
      {enableCrop && !multiple ? (
        <ImgCrop
          aspect={ratio}
          cropShape={cropShape}
          quality={0.92}
          modalTitle="Adjust image"
        >
          {uploadElement}
        </ImgCrop>
      ) : (
        uploadElement
      )}
    </div>
  );
}
